import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ShieldCheck, ChevronDown, ChevronUp, ArrowLeft, CheckCircle2 } from "lucide-react";
import { virksomhedsinfo } from "../data";

export default function LigtornBehandling() {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  useEffect(() => {
    // Sæt sidetitel og meta-beskrivelse
    document.title = "Behandling af ligtorn i Odense | Fjern ligtorn fra 260kr.";
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      "content",
      "Få målrettet behandling af ligtorn i Odense hos RG Fodpleje. Samme behandler hver gang, grundig beskæring og hulbor. Book nemt online i dag."
    );

    // Open Graph Tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement("meta");
      ogTitle.setAttribute("property", "og:title");
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute("content", "Behandling af ligtorn i Odense | Fjern ligtorn fra 260kr.");

    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement("meta");
      ogDesc.setAttribute("property", "og:description");
      document.head.appendChild(ogDesc);
    }
    ogDesc.setAttribute(
      "content",
      "Få målrettet behandling af ligtorn i Odense hos RG Fodpleje. Samme behandler hver gang, grundig beskæring og hulbor. Book nemt online i dag."
    );

    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement("meta");
      ogImage.setAttribute("property", "og:image");
      document.head.appendChild(ogImage);
    }
    ogImage.setAttribute(
      "content",
      "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=80,format=auto/assets/fc34e8db-fc4d-4e5c-ac32-f5583f3c3cfe/behandling-af-ligtorn.png"
    );

    // Indsæt JSON-LD Schema Markup
    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.id = "schema-faq-ligtorn";
    schemaScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Hvordan foregår behandling af ligtorn?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Hos RG Fodpleje foregår behandling af ligtorn ved, at den hårde hud og ligtornen beskæres omhyggeligt. Derefter anvendes et hulbor til at arbejde målrettet med den centrale del i dybden. Behandlingen tilpasses det område, der giver gener."
          }
        },
        {
          "@type": "Question",
          "name": "Hvornår kan fjernelse af ligtorn være relevant?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Fjernelse af ligtorn kan være relevant, hvis et afgrænset område med hård hud er ømt ved belastning, vender tilbage eller generer, når skoen trykker. Hos RG Fodpleje kan ligtornen behandles selvstændigt eller som en del af en almindelig fodbehandling."
          }
        },
        {
          "@type": "Question",
          "name": "Hvad bruges et hulbor til ved ligtornbehandling?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Et hulbor er et præcist redskab, som RG Fodpleje bruger til at arbejde med den centrale del af ligtornen efter beskæring af den hårde hud. Denne ligtorn behandling udføres målrettet i dybden og tilpasses det område, som giver gener."
          }
        },
        {
          "@type": "Question",
          "name": "Hvad koster det at få fjernet en ligtorn?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "En selvstændig behandling af ligtorn hos RG Fodpleje koster 260 kr. Er der også behov for neglepleje og behandling af hård hud, koster en almindelig fodbehandling 400 kr., og her kan eventuel fjernelse af ligtorn indgå."
          }
        },
        {
          "@type": "Question",
          "name": "Kan en ligtorn komme igen efter behandling?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ja, en ligtorn kan vende tilbage, hvis huden fortsat udsættes for det samme tryk eller den samme friktion. RG Fodpleje lover derfor ikke en varig løsning efter ét besøg. Sko med god plads, strømper uden generende folder og regelmæssig fodpleje kan være med til at mindske belastningen."
          }
        },
        {
          "@type": "Question",
          "name": "Hvordan kan man kende forskel på en ligtorn, en fodvorte og hård hud?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "En ligtorn, en fodvorte og almindelig hård hud kan ligne hinanden, men kræver ikke nødvendigvis samme behandling. RG Fodpleje anbefaler, at du undgår selv at skære i området, hvis du er usikker. Ved sår, særlige helbredsproblemer eller tvivl om forandringer på foden bør du søge relevant sundhedsfaglig rådgivning."
          }
        },
        {
          "@type": "Question",
          "name": "Hvor kan man booke professionel ligtornbehandling?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "RG Fodpleje tilbyder behandling af ligtorn i Odense med online booking og den samme behandler ved hvert besøg. Klinikken er relevant ved søgninger som behandling af ligtorn Odense og fjernelse af ligtorn Odense. Værktøjet steriliseres i autoklave efter brug, og klinikken er godkendt af SADF."
          }
        }
      ]
    });
    
    // Fjern eksisterende script hvis det findes
    const oldScript = document.getElementById("schema-faq-ligtorn");
    if (oldScript) {
      oldScript.remove();
    }
    document.head.appendChild(schemaScript);

    return () => {
      const scriptToRemove = document.getElementById("schema-faq-ligtorn");
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const faqItems = [
    {
      question: "Hvordan foregår behandling af ligtorn?",
      answer: "Hos RG Fodpleje foregår behandling af ligtorn ved, at den hårde hud og ligtornen beskæres omhyggeligt. Derefter anvendes et hulbor til at arbejde målrettet med den centrale del i dybden. Behandlingen tilpasses det område, der giver gener."
    },
    {
      question: "Hvornår kan fjernelse af ligtorn være relevant?",
      answer: "Fjernelse af ligtorn kan være relevant, hvis et afgrænset område med hård hud er ømt ved belastning, vender tilbage eller generer, når skoen trykker. Hos RG Fodpleje kan ligtornen behandles selvstændigt eller som en del af en almindelig fodbehandling."
    },
    {
      question: "Hvad bruges et hulbor til ved ligtornbehandling?",
      answer: "Et hulbor er et præcist redskab, som RG Fodpleje bruger til at arbejde med den centrale del af ligtornen efter beskæring af den hårde hud. Denne ligtorn behandling udføres målrettet i dybden og tilpasses det område, som giver gener."
    },
    {
      question: "Hvad koster det at få fjernet en ligtorn?",
      answer: "En selvstændig behandling af ligtorn hos RG Fodpleje koster 260 kr. Er der også behov for neglepleje og behandling af hård hud, koster en almindelig fodbehandling 400 kr., og her kan eventuel fjernelse af ligtorn indgå."
    },
    {
      question: "Kan en ligtorn komme igen efter behandling?",
      answer: "Ja, en ligtorn kan vende tilbage, hvis huden fortsat udsættes for det samme tryk eller den samme friktion. RG Fodpleje lover derfor ikke en varig løsning efter ét besøg. Sko med god plads, strømper uden generende folder og regelmæssig fodpleje kan være med til at mindske belastningen."
    },
    {
      question: "Hvordan kan man kende forskel på en ligtorn, en fodvorte og hård hud?",
      answer: "En ligtorn, en fodvorte og almindelig hård hud kan ligne hinanden, men kræver ikke nødvendigvis samme behandling. RG Fodpleje anbefaler, at du undgår selv at skære i området, hvis du er usikker. Ved sår, særlige helbredsproblemer eller tvivl om forandringer på foden bør du søge relevant sundhedsfaglig rådgivning."
    },
    {
      question: "Hvor kan man booke professionel ligtornbehandling?",
      answer: "RG Fodpleje tilbyder behandling af ligtorn i Odense med online booking og den samme behandler ved hvert besøg. Klinikken er relevant ved søgninger som behandling af ligtorn Odense og fjernelse af ligtorn Odense. Værktøjet steriliseres i autoklave efter brug, og klinikken er godkendt af SADF."
    }
  ];

  return (
    <div className="bg-[#FAF8F5] text-[#2D2A26] min-h-screen py-10 sm:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Tilbage link */}
        <div className="mb-8">
          <Link
            to="/behandlinger"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#4A5D4E] hover:text-[#3B4C3E] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Tilbage til behandlinger</span>
          </Link>
        </div>

        {/* Hero / Header */}
        <header className="mb-12 space-y-4 border-b border-[#EAE5DF] pb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E8EFEA] text-[#4A5D4E] text-xs font-semibold uppercase tracking-wider">
            <ShieldCheck className="h-4 w-4" />
            <span>Målrettet Fodpleje i Odense</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A26] leading-tight">
            Behandling af ligtorn i Odense med grundig fodpleje
          </h1>
          <p className="text-[#6B6661] text-lg font-light leading-relaxed">
            En ligtorn kan gøre det ubehageligt at gå, stå og bruge dine sædvanlige sko. Hos RG Fodpleje får du målrettet behandling af ligtorn i Odense, hvor den hårde hud beskæres, og der arbejdes med hulbor i dybden. Du møder altid den samme behandler i klinikken.
          </p>

          <div className="pt-4 flex flex-wrap gap-4 items-center">
            <a
              href="https://rg-fodpleje.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#4A5D4E] hover:bg-[#3B4C3E] text-[#FAF8F5] px-6 py-3 rounded-full text-sm font-medium tracking-wide transition-colors shadow-sm"
            >
              <Calendar className="h-4 w-4" />
              <span>Book tid online (260 kr.)</span>
            </a>
            <span className="text-xs text-[#6B6661] font-medium">
              SADF-godkendt klinik • Autoklave sterilisation
            </span>
          </div>
        </header>

        {/* Hovedindhold */}
        <article className="space-y-12">
          
          {/* Billede + H2 sektion 1 */}
          <section className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7 space-y-4">
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#2D2A26]">
                Hvad er en ligtorn, og hvorfor bør du reagere på trykket?
              </h2>
              <p className="text-[#6B6661] font-light leading-relaxed">
                En ligtorn er et lille og afgrænset område med fortykket hud, som ofte opstår, når huden bliver udsat for vedvarende tryk eller friktion. Den kan sidde på tæerne, mellem tæerne eller under foden. Generne mærkes ofte mest, når området belastes i sko eller under gang.
              </p>
              <p className="text-[#6B6661] font-light leading-relaxed">
                Det er ikke altid let selv at vurdere, om et område er en ligtorn, en fodvorte eller almindelig hård hud. De kan ligne hinanden, men kræver ikke nødvendigvis samme behandling. Undgå derfor at skære i huden selv, hvis du er usikker på, hvad der giver generne.
              </p>
            </div>
            
            <div className="md:col-span-5">
              <div className="bg-white p-2 sm:p-3 rounded-2xl border border-[#EAE5DF] shadow-sm">
                <div className="relative rounded-xl overflow-hidden bg-[#FAF8F5]">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/fc34e8db-fc4d-4e5c-ac32-f5583f3c3cfe/behandling-af-ligtorn.png"
                    alt="Behandling af ligtorn og beskæring af hård hud hos RG Fodpleje"
                    className="w-full h-64 sm:h-72 md:h-80 object-cover object-center hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-4 text-white text-xs">
                    <p className="font-medium">Eksempel på ligtorn og skånsom beskæring</p>
                    <p className="text-white/80 text-[11px] font-light">Målrettet dybdegående behandling hos RG Fodpleje</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* H3 + List */}
          <section className="bg-white rounded-2xl border border-[#EAE5DF] p-6 sm:p-8 shadow-sm space-y-6">
            <h3 className="font-serif text-xl sm:text-2xl font-semibold text-[#2D2A26]">
              Hvornår giver fjernelse af ligtorn mening?
            </h3>
            <p className="text-[#6B6661] font-light leading-relaxed">
              Fjernelse af ligtorn kan være relevant, når den hårde hud bliver ved med at genere, eller når du oplever et koncentreret tryk på det samme sted. En behandling kan reducere den hårde hud omkring området, men det er også vigtigt at være opmærksom på den belastning, som har skabt problemet.
            </p>

            <ul className="space-y-3 pt-2">
              <li className="flex items-start gap-3 text-[#2D2A26] font-medium text-sm sm:text-base">
                <CheckCircle2 className="h-5 w-5 text-[#4A5D4E] shrink-0 mt-0.5" />
                <span>Et afgrænset område med hård hud, som er ømt ved belastning</span>
              </li>
              <li className="flex items-start gap-3 text-[#2D2A26] font-medium text-sm sm:text-base">
                <CheckCircle2 className="h-5 w-5 text-[#4A5D4E] shrink-0 mt-0.5" />
                <span>Gener når skoen trykker på et bestemt sted på foden</span>
              </li>
              <li className="flex items-start gap-3 text-[#2D2A26] font-medium text-sm sm:text-base">
                <CheckCircle2 className="h-5 w-5 text-[#4A5D4E] shrink-0 mt-0.5" />
                <span>Hård hud som vender tilbage på det samme område</span>
              </li>
            </ul>
          </section>

          {/* Sektion 2 */}
          <section className="space-y-4">
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#2D2A26]">
              Sådan foregår behandling af ligtorn hos RG Fodpleje
            </h2>
            <p className="text-[#6B6661] font-light leading-relaxed">
              Behandlingen hos RG Fodpleje består af en omhyggelig beskæring af ligtornen. Derefter anvendes et hulbor, så der kan arbejdes målrettet i dybden. Formålet er at fjerne den koncentrerede hårde hud uden at love et bestemt resultat eller en varig løsning efter et enkelt besøg.
            </p>
          </section>

          {/* Sektion 3 med hulbor list */}
          <section className="bg-[#F3EFEA] rounded-2xl border border-[#EAE5DF] p-6 sm:p-8 space-y-6">
            <h3 className="font-serif text-xl sm:text-2xl font-semibold text-[#2D2A26]">
              Hvad gør hulboret i behandlingen?
            </h3>
            <p className="text-[#6B6661] font-light leading-relaxed">
              Et hulbor er et præcist redskab, der bruges til at arbejde med den centrale del af ligtornen. Behandlingen tilpasses det område, som giver dig gener. Hvor hurtigt en ligtorn vender tilbage, afhænger blandt andet af, om huden fortsat bliver udsat for det samme tryk eller den samme friktion.
            </p>

            <ul className="space-y-3 pt-2">
              <li className="flex items-start gap-3 text-[#2D2A26] font-medium text-sm sm:text-base">
                <CheckCircle2 className="h-5 w-5 text-[#4A5D4E] shrink-0 mt-0.5" />
                <span>Beskæring af den hårde hud og ligtornen</span>
              </li>
              <li className="flex items-start gap-3 text-[#2D2A26] font-medium text-sm sm:text-base">
                <CheckCircle2 className="h-5 w-5 text-[#4A5D4E] shrink-0 mt-0.5" />
                <span>Brug af hulbor til at arbejde i dybden</span>
              </li>
              <li className="flex items-start gap-3 text-[#2D2A26] font-medium text-sm sm:text-base">
                <CheckCircle2 className="h-5 w-5 text-[#4A5D4E] shrink-0 mt-0.5" />
                <span>En selvstændig behandling til 260 kr.</span>
              </li>
            </ul>
          </section>

          {/* Sektion 4 */}
          <section className="space-y-4">
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#2D2A26]">
              Hvordan mindsker du risikoen for nye ligtorne?
            </h2>
            <p className="text-[#6B6661] font-light leading-relaxed">
              Efter fjernelse af ligtorn er det en god ide at være opmærksom på, hvorfor området bliver belastet. Hvis det samme tryk fortsætter, kan den hårde hud dannes igen. Smalle sko, sømme, hårde kanter og manglende plads omkring tæerne kan være med til at øge belastningen.
            </p>
            <p className="text-[#6B6661] font-light leading-relaxed">
              Vælg gerne sko med god plads til tæerne, og mærk efter om foden bliver klemt bestemte steder. Strømper uden generende folder kan også gøre en forskel i hverdagen. Regelmæssig fodpleje hjælper dig samtidig med at opdage hård hud, før området udvikler sig og bliver mere generende.
            </p>
            <p className="text-[#6B6661] font-light leading-relaxed">
              Du bør ikke forsøge at skære en ligtorn væk med kniv, saks eller andre skarpe redskaber. Det kan beskadige den raske hud omkring området. Er du i tvivl om forandringer på foden, eller har du sår og særlige helbredsproblemer, bør du søge relevant sundhedsfaglig rådgivning.
            </p>
          </section>

          {/* Sektion 5 */}
          <section className="space-y-4">
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#2D2A26]">
              Hvorfor vælge personlig ligtornbehandling i Odense?
            </h2>
            <p className="text-[#6B6661] font-light leading-relaxed">
              RG Fodpleje er en enkeltmandsklinik, så du møder den samme behandler ved hvert besøg. Det giver et genkendeligt og personligt forløb, hvis du får brug for behandling igen. Klinikken har lyse og pæne lokaler med fokus på en rolig oplevelse og tydelig information om behandlingen.
            </p>
            <p className="text-[#6B6661] font-light leading-relaxed">
              Hygiejne er en fast del af arbejdet i klinikken. Alt værktøj steriliseres i en autoklave efter brug, og der anvendes godkendte metoder. Klinikken er desuden godkendt af SADF. Det giver klare rammer omkring din behandling af ligtorn og den øvrige fodpleje.
            </p>
          </section>

          {/* Sektion 6 */}
          <section className="bg-white rounded-2xl border border-[#EAE5DF] p-6 sm:p-8 shadow-sm space-y-4">
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#2D2A26]">
              Book fjernelse af ligtorn i Odense
            </h2>
            <p className="text-[#6B6661] font-light leading-relaxed">
              Hvis ligtornen er dit eneste problem, kan du vælge den selvstændige behandling til 260 kr. Har du også behov for neglepleje og behandling af hård hud, kan du vælge en almindelig fodbehandling til 400 kr. Her kan eventuel fjernelse af ligtorne indgå som en del af behandlingen.
            </p>
            <p className="text-[#6B6661] font-light leading-relaxed">
              Du kan booke din tid online, når det passer dig. Søger du behandling af ligtorn i Odense eller hjælp til tilbagevendende hård hud, står RG Fodpleje klar til at tage imod dig i klinikken. Her får du et overskueligt behandlingsforløb med samme behandler fra gang til gang.
            </p>
            <div className="pt-4">
              <a
                href="https://rg-fodpleje.planway.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#4A5D4E] hover:bg-[#3B4C3E] text-[#FAF8F5] px-6 py-3 rounded-full text-sm font-medium tracking-wide transition-colors shadow-sm"
              >
                <Calendar className="h-4 w-4" />
                <span>Book tid online nu</span>
              </a>
            </div>
          </section>

          {/* FAQ Sektion */}
          <section className="pt-8 border-t border-[#EAE5DF] space-y-6">
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#2D2A26]">
              Ofte stillede spørgsmål
            </h3>
            
            <div className="space-y-4">
              {faqItems.map((item, index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-xl border border-[#EAE5DF] overflow-hidden transition-all shadow-xs"
                  >
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full px-6 py-4 text-left flex justify-between items-center gap-4 hover:bg-[#FAF8F5] transition-colors"
                    >
                      <h4 className="font-medium text-[#2D2A26] text-base sm:text-lg">
                        {item.question}
                      </h4>
                      <span className="text-[#4A5D4E] shrink-0">
                        {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-5 text-[#6B6661] font-light leading-relaxed border-t border-[#F3EFEA] pt-3 text-sm sm:text-base">
                        <p>{item.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

        </article>

        {/* CTA Boks i bunden */}
        <div className="mt-16 bg-[#4A5D4E] text-[#FAF8F5] rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-md">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold">
            Klar til at slippe af med ligtornsmerter?
          </h2>
          <p className="text-[#FAF8F5]/90 max-w-xl mx-auto font-light leading-relaxed">
            Besøg RG Fodpleje på {virksomhedsinfo.adresse}. Book din tid online i dag og få en grundig og behagelig behandling af samme behandler hver gang.
          </p>
          <div className="pt-2 flex flex-wrap justify-center gap-4">
            <a
              href="https://rg-fodpleje.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#FAF8F5] hover:bg-white text-[#4A5D4E] px-8 py-3.5 rounded-full font-medium tracking-wide transition-colors shadow-sm"
            >
              <Calendar className="h-4 w-4" />
              <span>Book tid online</span>
            </a>
            <a
              href={`tel:${virksomhedsinfo.telefon.replace(/\s+/g, '')}`}
              className="inline-flex items-center gap-2 border border-[#FAF8F5]/40 hover:bg-[#FAF8F5]/10 text-[#FAF8F5] px-6 py-3.5 rounded-full font-medium tracking-wide transition-colors"
            >
              <span>Ring {virksomhedsinfo.telefon}</span>
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}
